import React, { useRef, useState } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";
import { generateAdmissionNo, getCourseDuration, getSalutation } from "./utils/letterUtils";
import LetterHeader from "./LetterHeader";

export default function Acknowledgement({ student, branchData }) {
  const printRef = useRef();

  const [formData, setFormData] = useState({
    refNo: generateAdmissionNo({
      prefix: "RBT",
      course: student?.selectedCourse,
      studentId: student?.studentId,
    }),
    date: new Date().toLocaleDateString(),
    studentName:
      [student?.firstName, student?.middleName, student?.lastName]
        .filter(Boolean)
        .join(" ") || "",
    parentName: student?.fatherName || "",
    course: student?.selectedCourse || "",
    academicYear: student?.academicYear || "",
    duration: getCourseDuration(
      student?.selectedCourse || "",
      student?.academicYear || ""
    ),
    admissionNo: student?.studentId || "",

    university: "Rajiv Gandhi University of Health Sciences",
    recognition:
      "KNC Govt. of Karnataka & Indian Nursing Council, New Delhi",
    instituteName: "REHOBOTH INSTITUTE OF NURSING",
    approvalLine:
      "(Recognized by Govt. of Karnataka & Indian Nursing Council)",
    address: "Bangalore, Karnataka",
  });

  const [documents, setDocuments] = useState([
    "Original SSLC Certificate",
    "Original +2 Certificate",
    "Original T.C.",
    "Original Migration Certificate",
    "Copy of Aadhar/National ID Card",
    "4 Copies of Passport Size Photo",
    "Conduct Certificate",
  ]);

  const [selectedDocs, setSelectedDocs] = useState([...documents]);
  const [newDoc, setNewDoc] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDocChange = (doc) => {
    if (selectedDocs.includes(doc)) {
      setSelectedDocs(selectedDocs.filter((d) => d !== doc));
    } else {
      setSelectedDocs([...selectedDocs, doc]);
    }
  };

  const handleAddDoc = () => {
    if (newDoc.trim() && !documents.includes(newDoc)) {
      setDocuments([...documents, newDoc]);
      setSelectedDocs([...selectedDocs, newDoc]);
      setNewDoc("");
    }
  };

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open("", "", "width=900,height=700");
    win.document.write(`
      <html>
        <head>
          <title>Acknowledgement Letter Print</title>
          <link
            rel="stylesheet"
            href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
          />
          <style>
            @media print {
              body { 
                font-family: 'Times New Roman', Times, serif; 
                padding: 10px 20px 20px; 
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .print-container { line-height: 1.8; }
              .text-center { text-align: center; }
              .text-end { text-align: right; }
            }
            body { 
              font-family: 'Times New Roman', Times, serif; 
              padding: 20px; 
            }
            .print-container { line-height: 1.8; }
          </style>
        </head>
        <body>
          <div class="print-container">${content}</div>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
    }, 500);
  };

  const { salute, title, possessive } = getSalutation(student?.gender);

  return (
    <Container className="py-4">
      {/* FORM CONTROL PANEL */}
      <Form className="mb-4">
        <Row className="mb-2">
          <Col>
            <Form.Label className="small text-muted">Ref No.</Form.Label>
            <Form.Control name="refNo" value={formData.refNo} onChange={handleChange} />
          </Col>
          <Col>
            <Form.Label className="small text-muted">Date</Form.Label>
            <Form.Control name="date" value={formData.date} onChange={handleChange} />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Label className="small text-muted">Student Name</Form.Label>
            <Form.Control name="studentName" value={formData.studentName} onChange={handleChange} />
          </Col>
          <Col>
            <Form.Label className="small text-muted">Parent / Father Name</Form.Label>
            <Form.Control name="parentName" value={formData.parentName} onChange={handleChange} />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Label className="small text-muted">Course</Form.Label>
            <Form.Control name="course" value={formData.course} onChange={handleChange} />
          </Col>
          <Col>
            <Form.Label className="small text-muted">Academic Year</Form.Label>
            <Form.Control name="academicYear" value={formData.academicYear} onChange={handleChange} />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Label className="small text-muted">Course Duration</Form.Label>
            <Form.Control name="duration" value={formData.duration} onChange={handleChange} />
          </Col>
          <Col>
            <Form.Label className="small text-muted">Admission No.</Form.Label>
            <Form.Control name="admissionNo" value={formData.admissionNo} onChange={handleChange} />
          </Col>
        </Row>
      </Form>

      {/* DOCUMENT SELECTION CHECKBOXES */}
      <h5 className="mt-4">Document Checklist</h5>
      {documents.map((doc, idx) => (
        <Form.Check
          key={idx}
          type="checkbox"
          label={doc}
          checked={selectedDocs.includes(doc)}
          onChange={() => handleDocChange(doc)}
        />
      ))}

      {/* ADD NEW DOCUMENT OPTION */}
      <Row className="mt-2">
        <Col>
          <Form.Control
            placeholder="Add custom document..."
            value={newDoc}
            onChange={(e) => setNewDoc(e.target.value)}
          />
        </Col>
        <Col xs="auto">
          <Button onClick={handleAddDoc}>Add Document</Button>
        </Col>
      </Row>

      {/* BULK SELECTION CONTROLS */}
      <Row className="mt-2">
        <Col xs="auto">
          <Button size="sm" onClick={() => setSelectedDocs(documents)}>
            Select All
          </Button>
        </Col>
        <Col xs="auto">
          <Button size="sm" variant="secondary" onClick={() => setSelectedDocs([])}>
            Clear All
          </Button>
        </Col>
      </Row>

      {/* ACTION PRINT BUTTON */}
      <div className="text-end mt-4 mb-3">
        <Button variant="primary" onClick={handlePrint}>
          Print Letter
        </Button>
      </div>

      {/* PRINTABLE LETTER CONTAINER */}
      <div
        ref={printRef}
        style={{ padding: 25, border: "1px solid #000" }}
        className="print-container bg-white"
      >
        <LetterHeader
          branchData={branchData}
          useOldAddress={false}
          badgeText=""
          badgeBg="primary"
          paperSize="A4"
        />

        {/* Reference & Date */}
        <Row className="mt-5 pt-2">
          <Col>
            <strong>{formData.refNo}</strong>
          </Col>
          <Col className="text-end">
            <strong>{formData.date}</strong>
          </Col>
        </Row>

        {/* Document Title */}
        <Row className="mt-4">
          <Col className="text-center">
            <h2 style={{ marginTop: "30px" }}>
              <u>Acknowledgement Letter</u>
            </h2>
          </Col>
        </Row>

        {/* Letter Body Content */}
        <Row className="mt-4">
          <Col>
            <p style={{ marginTop: "20px" }}>
              This is to inform you that{" "}
              <strong>
                {salute} {formData.studentName}
              </strong>{" "}
              {title} Mr. <strong>{formData.parentName}</strong> has been admitted to
              our College in <strong>{formData.course}</strong> course from the
              academic year <strong>{formData.academicYear}</strong>.
            </p>

            <p>
              The duration of the course is <strong>{formData.duration}</strong>.{" "}
              {possessive} admission No. is <strong>{formData.admissionNo}</strong>.
            </p>

            <p style={{ marginTop: "30px" }}>
              We acknowledge the receipt of the following original documents from the
              candidate:
            </p>

            <ol style={{ paddingLeft: "80px" }}>
              {selectedDocs.map((doc, idx) => (
                <li key={idx} style={{ marginBottom: "8px" }}>
                  {doc}
                </li>
              ))}
            </ol>
          </Col>
        </Row>

        {/* Footer Signature */}
        <Row className="mt-5" style={{ paddingTop: "40px" }}>
          <Col className="text-end">
            <p>
              <strong>ADMIN</strong>
            </p>
          </Col>
        </Row>
      </div>
    </Container>
  );
}